/**
 * Campaign-link helpers for marketing/landing pages.
 *
 * buildAuthHref() constructs an internal `/auth` signup link that:
 *   1. Uses the real signup param the Auth page reads (`mode=signup`).
 *   2. Tags the click with `src=<page-location>` for internal funnel analysis.
 *   3. Forwards any inbound `utm_*`/`gclid` currently on the URL so they
 *      survive the navigation to /auth (important for Google Ads offline
 *      conversion import later).
 *
 * This intentionally does NOT touch /auth, the billing flow, or the existing
 * consent-gated cookie capture in src/lib/gclidCapture.ts. That utility remains
 * the canonical attribution path (URL params -> first-party cookie -> profiles
 * row on auth). This helper only keeps the query string consistent end-to-end.
 */

/** Inbound params we forward from the current URL onto /auth links. */
const FORWARDED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
] as const;

export interface AuthHrefOptions {
  /** Defaults to "signup" — the mode the Auth page switches to. */
  mode?: 'signup' | 'signin';
  /**
   * Explicit search string to read forwarded params from. Defaults to
   * window.location.search. Exposed for testing / SSR safety.
   */
  search?: string;
}

/**
 * Build an internal `/auth` href for a campaign CTA.
 *
 * @param src  Stable identifier for the CTA location, e.g. "agencies-hero".
 */
export function buildAuthHref(src: string, options: AuthHrefOptions = {}): string {
  const params = new URLSearchParams();
  params.set('mode', options.mode ?? 'signup');

  if (src) {
    params.set('src', src);
  }

  const search =
    options.search ??
    (typeof window !== 'undefined' ? window.location.search : '');

  if (search) {
    const inbound = new URLSearchParams(search);
    for (const key of FORWARDED_PARAMS) {
      const value = inbound.get(key);
      if (value) {
        params.set(key, value);
      }
    }
  }

  return `/auth?${params.toString()}`;
}
